'use client';

import { Box, Stack, Theme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';
import { BrandedDotLoader } from '../BrandedDotLoader';
import { Logo } from '../Logo';

export type LoadingScreenLayout = 'fullPage' | 'contentArea';

export type LoadingScreenProps = {
  'data-testid'?: string;
  /**
   * `'fullPage'` (default) fills the viewport (`minHeight: 100vh`), matching the loading screen
   * AuthenticatedContainer has always shown while confirming sign-in status.
   *
   * `'contentArea'` instead fills whatever container renders it, with no viewport-height rule, for
   * rendering this loader inside an already-chromed page rather than in place of the whole page.
   */
  layout?: LoadingScreenLayout;
};

/**
 * Generic branded loading screen (VerifiMe mark + dot loader). `layout="fullPage"` (the default) is
 * the full-page screen AuthenticatedContainer renders while confirming sign-in status - so
 * unauthenticated visitors never see a flash of gated content before the auth check completes and
 * redirects them. `layout="contentArea"` renders the same branded loading treatment sized to fill
 * its container instead, for use inside a page rather than in place of the whole page.
 */
function LoadingScreenComponent({
  'data-testid': dataTestId = 'loading-screen',
  layout = 'fullPage',
}: Readonly<LoadingScreenProps>): ReactElement {
  return (
    <Stack
      data-testid={dataTestId}
      role="status"
      aria-live="polite"
      aria-label="Loading"
      sx={(theme: Theme) => ({
        ...(layout === 'fullPage'
          ? { minHeight: '100vh' }
          // What actually fills the real customer-portal Layout content container (the `<Box
          // sx={{ flex: 1 }}>` in apps/customer-portal/src/components/Layout.tsx) is `height:
          // 100%`: that Box is a plain block element (no `display: flex/grid` of its own), so it
          // is a flex ITEM of its row, stretched to a definite height by the row's default
          // `align-items: stretch`, and a definite ancestor height is exactly what `height: 100%`
          // needs to resolve. `flex: '1 1 auto'` and `minHeight: 0` are for the different case
          // where the immediate parent is itself a flex/grid container sizing its children by
          // growth rather than a definite cross size; the `flex` shorthand has no effect on an
          // element that is not itself a flex item, so on a plain block ancestor like Layout's
          // Box, both are inert rather than harmful, and `height: 100%` alone does the work.
          : { flex: '1 1 auto', minHeight: 0, height: '100%' }),
        width: '100%',
        backgroundColor: theme.palette.background.default,
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.semantic.spacing.base,
        boxSizing: 'border-box',
      })}
    >
      <Logo layout="markOnly" size="large" colorMode="auto" />

      <BrandedDotLoader />

      {/* Visually-hidden text so screen readers that surface a status region's
          text content (rather than relying on aria-label alone) still announce
          the loading state. */}
      <Box
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Loading
      </Box>
    </Stack>
  );
}

export const LoadingScreen = withClarityTheme(LoadingScreenComponent);
